import React, { useState, useEffect, useMemo } from 'react';
import {
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  Collapse,
  IconButton,
  Paper,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  TextField,
  InputAdornment,
} from '@mui/material';
import {
  ExpandMore,
  ChevronRight,
  FolderOutlined,
  FolderOpenOutlined,
  Search as SearchIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import permissionsAPI from '../../../services/api/permissions.api';

const PermissionTreeView = ({ roleId, onPermissionsChange, initialPermissions = [] }) => {
  const [menuTree, setMenuTree] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [expandedMenus, setExpandedMenus] = useState(new Set());
  const [selectedPermissions, setSelectedPermissions] = useState(new Set(initialPermissions));
  const [searchTerm, setSearchTerm] = useState('');

  // Available actions with labels
  const actions = [
    { value: 'view', label: 'View', color: 'primary' },
    { value: 'create', label: 'Create', color: 'success' },
    { value: 'update', label: 'Update', color: 'warning' },
    { value: 'delete', label: 'Delete', color: 'error' },
    { value: 'approve', label: 'Approve', color: 'info' },
    { value: 'export', label: 'Export', color: 'secondary' },
    { value: 'import', label: 'Import', color: 'default' },
    { value: 'manage', label: 'Manage', color: 'primary' },
  ];

  useEffect(() => {
    if (roleId) {
      loadRolePermissions();
    }
  }, [roleId]);

  useEffect(() => {
    // Notify parent of permission changes
    if (onPermissionsChange) {
      onPermissionsChange(Array.from(selectedPermissions));
    }
  }, [selectedPermissions, onPermissionsChange]);

  const loadRolePermissions = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await permissionsAPI.getRolePermissions(roleId);
      
      if (response.success && response.data) {
        const { menus } = response.data;
        setMenuTree(menus || []);
        
        // Extract selected permission IDs
        const permissionIds = extractPermissionIds(menus || []);
        setSelectedPermissions(new Set(permissionIds));
        
        // Auto-expand all menus initially
        const allMenuIds = extractMenuIds(menus || []);
        setExpandedMenus(new Set(allMenuIds));
      }
    } catch (err) {
      console.error('Error loading role permissions:', err);
      setError(err.message || 'Failed to load permissions');
    } finally {
      setLoading(false);
    }
  };

  const extractPermissionIds = (menus) => {
    const ids = [];
    menus.forEach(menu => {
      menu.permissions?.forEach(perm => {
        if (perm.hasPermission) {
          ids.push(perm.id);
        }
      });
      if (menu.children) {
        ids.push(...extractPermissionIds(menu.children));
      }
    });
    return ids;
  };

  const extractMenuIds = (menus) => {
    const ids = [];
    menus.forEach(menu => {
      ids.push(menu.id);
      if (menu.children) {
        ids.push(...extractMenuIds(menu.children));
      }
    });
    return ids;
  };

  const handleToggleMenu = (menuId) => {
    setExpandedMenus(prev => {
      const newSet = new Set(prev);
      if (newSet.has(menuId)) {
        newSet.delete(menuId);
      } else {
        newSet.add(menuId);
      }
      return newSet;
    });
  };

  const handlePermissionToggle = (permissionId, checked) => {
    setSelectedPermissions(prev => {
      const newSet = new Set(prev);
      if (checked) {
        newSet.add(permissionId);
      } else {
        newSet.delete(permissionId);
      }
      return newSet;
    });
  };

  const handleSelectAllInMenu = (menu, checked) => {
    const permissionIds = [];
    
    const collectPermissions = (m) => {
      m.permissions?.forEach(perm => {
        permissionIds.push(perm.id);
      });
      m.children?.forEach(child => collectPermissions(child));
    };
    
    collectPermissions(menu);
    
    setSelectedPermissions(prev => {
      const newSet = new Set(prev);
      if (checked) {
        permissionIds.forEach(id => newSet.add(id));
      } else {
        permissionIds.forEach(id => newSet.delete(id));
      }
      return newSet;
    });
  };

  const isMenuFullySelected = (menu) => {
    const allPermissionIds = [];
    const collectPermissions = (m) => {
      m.permissions?.forEach(perm => allPermissionIds.push(perm.id));
      m.children?.forEach(child => collectPermissions(child));
    };
    collectPermissions(menu);
    
    if (allPermissionIds.length === 0) return false;
    return allPermissionIds.every(id => selectedPermissions.has(id));
  };

  const isMenuPartiallySelected = (menu) => {
    const allPermissionIds = [];
    const collectPermissions = (m) => {
      m.permissions?.forEach(perm => allPermissionIds.push(perm.id));
      m.children?.forEach(child => collectPermissions(child));
    };
    collectPermissions(menu);
    
    if (allPermissionIds.length === 0) return false;
    const selectedCount = allPermissionIds.filter(id => selectedPermissions.has(id)).length;
    return selectedCount > 0 && selectedCount < allPermissionIds.length;
  };

  // Search synonyms: "travel report" should match expense report / travel advance / travel request approvals
  const normalizeSearchForMatch = (term) => {
    const t = term.toLowerCase().trim();
    if (!t) return [];
    const parts = [t];
    if (t.includes('travel report')) {
      parts.push('expense report', 'travel advance', 'travel request', 'approval');
    }
    if (t.includes('travel advance')) parts.push('travel_advance_approvals');
    if (t.includes('expense report')) parts.push('expense_report_approvals');
    if (t.includes('travel request')) parts.push('travel_request_approvals');
    return parts;
  };

  const searchTermMatches = (lowerSearch, label, name, description) => {
    const searchParts = normalizeSearchForMatch(lowerSearch);
    const labelL = (label || '').toLowerCase();
    const nameL = (name || '').toLowerCase();
    const descL = (description || '').toLowerCase();
    return searchParts.some(part => labelL.includes(part) || nameL.includes(part) || descL.includes(part));
  };

  // Filter menu tree based on search term
  const filterMenuTree = (menus, searchTerm) => {
    if (!searchTerm) return menus;

    const lowerSearch = searchTerm.toLowerCase();
    
    return menus
      .map(menu => {
        const menuMatches = 
          searchTermMatches(lowerSearch, menu.menu_label, menu.menu_name, null) ||
          menu.menu_label?.toLowerCase().includes(lowerSearch) ||
          menu.menu_name?.toLowerCase().includes(lowerSearch);
        
        const permissionMatches = menu.permissions?.some(perm => 
          searchTermMatches(lowerSearch, perm.description, perm.name, perm.action) ||
          perm.action?.toLowerCase().includes(lowerSearch) ||
          perm.description?.toLowerCase().includes(lowerSearch) ||
          perm.name?.toLowerCase().includes(lowerSearch)
        );
        
        const filteredChildren = menu.children ? filterMenuTree(menu.children, searchTerm) : [];
        const childrenMatch = filteredChildren.length > 0;
        
        if (menuMatches || permissionMatches || childrenMatch) {
          return {
            ...menu,
            children: filteredChildren,
            // Highlight matching permissions (including "travel report" -> expense/travel advance approvals)
            permissions: menu.permissions?.map(perm => ({
              ...perm,
              _matches: searchTermMatches(lowerSearch, perm.description, perm.name, perm.action) ||
                       perm.action?.toLowerCase().includes(lowerSearch) ||
                       perm.description?.toLowerCase().includes(lowerSearch) ||
                       perm.name?.toLowerCase().includes(lowerSearch)
            }))
          };
        }
        return null;
      })
      .filter(Boolean);
  };

  const filteredMenuTree = useMemo(() => {
    return filterMenuTree(menuTree, searchTerm);
  }, [menuTree, searchTerm]);

  // Auto-expand menus when searching
  useEffect(() => {
    if (searchTerm) {
      const allMenuIds = extractMenuIds(filteredMenuTree);
      setExpandedMenus(new Set(allMenuIds));
    }
  }, [searchTerm, filteredMenuTree]);

  const renderMenu = (menu, level = 0) => {
    const isExpanded = expandedMenus.has(menu.id);
    const hasChildren = menu.children && menu.children.length > 0;
    const isFullySelected = isMenuFullySelected(menu);
    const isPartiallySelected = isMenuPartiallySelected(menu);

    return (
      <Box key={menu.id} sx={{ ml: level * 2 }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            py: 0.5,
            px: 1,
            borderRadius: 1,
            '&:hover': {
              backgroundColor: 'action.hover',
            },
          }}
        >
          {/* Expand/Collapse Icon */}
          <IconButton
            size="small"
            onClick={() => handleToggleMenu(menu.id)}
            sx={{ mr: 0.5, visibility: hasChildren ? 'visible' : 'hidden' }}
          >
            {isExpanded ? <ExpandMore /> : <ChevronRight />}
          </IconButton>

          {/* Menu Icon */}
          {hasChildren ? (
            isExpanded ? (
              <FolderOpenOutlined sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
            ) : (
              <FolderOutlined sx={{ mr: 1, color: 'text.secondary', fontSize: 20 }} />
            )
          ) : (
            <Box sx={{ width: 20, mr: 1 }} />
          )}

          {/* Menu Select All Checkbox */}
          <FormControlLabel
            control={
              <Checkbox
                checked={isFullySelected}
                indeterminate={isPartiallySelected}
                onChange={(e) => handleSelectAllInMenu(menu, e.target.checked)}
                size="small"
              />
            }
            label={
              <Typography variant="body2" sx={{ fontWeight: level === 0 ? 600 : 400 }}>
                {menu.menu_label}
              </Typography>
            }
            sx={{ flex: 1, mr: 2 }}
          />

          {/* Action Checkboxes */}
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            {menu.permissions?.map(permission => {
              const action = actions.find(a => a.value === permission.action);
              const isHighlighted = permission._matches;
              // Friendly label: use description or name (e.g. expense_report_approvals:view -> "View Travel Expense Report Approvals")
              const chipLabel = permission.description
                ? permission.description
                : permission.name && permission.name.includes(':')
                  ? permission.name.replace(/_/g, ' ').replace(/:view$/i, ' (View)').replace(/:(\w+)$/, (_, a) => ` (${a})`)
                  : (action?.label || permission.action);
              return (
                <FormControlLabel
                  key={permission.id}
                  control={
                    <Checkbox
                      checked={selectedPermissions.has(permission.id)}
                      onChange={(e) => handlePermissionToggle(permission.id, e.target.checked)}
                      size="small"
                    />
                  }
                  label={
                    <Chip
                      label={chipLabel}
                      size="small"
                      color={action?.color || 'default'}
                      variant={selectedPermissions.has(permission.id) ? 'filled' : 'outlined'}
                      sx={{ 
                        height: 24, 
                        fontSize: '0.7rem',
                        maxWidth: 220,
                        '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' },
                        backgroundColor: isHighlighted && !selectedPermissions.has(permission.id) 
                          ? 'action.selected' 
                          : undefined,
                        fontWeight: isHighlighted ? 600 : undefined,
                      }}
                    />
                  }
                  sx={{ m: 0 }}
                />
              );
            })}
          </Box>
        </Box>

        {/* Children */}
        {hasChildren && (
          <Collapse in={isExpanded} timeout="auto" unmountOnExit>
            <Box sx={{ ml: 2 }}>
              {menu.children.map(child => renderMenu(child, level + 1))}
            </Box>
          </Collapse>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  if (!menuTree || menuTree.length === 0) {
    return (
      <Alert severity="info" sx={{ mb: 2 }}>
        No menus available. Please create menus first.
      </Alert>
    );
  }

  return (
    <Paper sx={{ p: 2, maxHeight: '70vh', display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ mb: 2 }}>
        <Typography variant="h6" gutterBottom>
          Menu Permissions
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Select permissions for this role. Checkboxes allow you to grant specific actions on each menu.
        </Typography>
        
        {/* Search Field */}
        <TextField
          fullWidth
          size="small"
          placeholder="Search menus or permissions..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" color="action" />
              </InputAdornment>
            ),
            endAdornment: searchTerm && (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  onClick={() => setSearchTerm('')}
                  edge="end"
                >
                  <ClearIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />
      </Box>

      <Divider sx={{ mb: 2 }} />

      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <Stack spacing={0.5}>
          {filteredMenuTree.length > 0 ? (
            filteredMenuTree.map(menu => renderMenu(menu))
          ) : (
            <Alert severity="info">
              {searchTerm 
                ? `No menus or permissions found matching "${searchTerm}"`
                : 'No menus available'}
            </Alert>
          )}
        </Stack>
      </Box>

      <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <Typography variant="caption" color="text.secondary">
          Selected: {selectedPermissions.size} permission(s)
          {searchTerm && filteredMenuTree.length > 0 && (
            <span> • Showing {filteredMenuTree.length} matching menu(s)</span>
          )}
        </Typography>
      </Box>
    </Paper>
  );
};

export default PermissionTreeView;
